// Single source of truth for password rules on the frontend. Mirrors the
// backend's password_strength_error (app/auth.py) — keep the two in sync.
export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordRule {
  id: string;
  /** Short label shown in the live requirements checklist. */
  label: string;
  /** Full sentence used as a submit-time error message. */
  error: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "upper",
    label: "An uppercase letter (A–Z)",
    error: "Password must contain an uppercase letter",
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    id: "lower",
    label: "A lowercase letter (a–z)",
    error: "Password must contain a lowercase letter",
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    id: "digit",
    label: "A number (0–9)",
    error: "Password must contain a number",
    test: (pw) => /\d/.test(pw),
  },
  {
    id: "symbol",
    label: "A symbol (!@#$…)",
    error: "Password must contain a symbol",
    test: (pw) => /[^A-Za-z0-9]/.test(pw),
  },
];

export const isPasswordStrong = (password: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(password));

/** First unmet rule's error message, or null when the password passes all rules. */
export const firstPasswordIssue = (password: string): string | null =>
  PASSWORD_RULES.find((rule) => !rule.test(password))?.error ?? null;
